// src/admin/hooks/useShortUrlBatches.js
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          reject(new DOMException('Aborted', 'AbortError'));
        },
        { once: true }
      );
    }
  });
}

function createWaitForPdfExportAndDownload(getPdfExport, fetchPdfDownload, triggerBrowserDownload) {
  return async function waitForPdfExportAndDownload(exportId, options = {}) {
    const intervalMs = options.intervalMs || 1500;
    const timeoutMs = options.timeoutMs || 60000;
    const signal = options.signal;
    const start = Date.now();

    while (Date.now() - start < timeoutMs) {
      if (signal?.aborted) {
        throw new DOMException('PDF export polling aborted', 'AbortError');
      }

      const current = await getPdfExport(exportId);
      if (current.status === 'succeeded') {
        const payload = await fetchPdfDownload(exportId);
        await triggerBrowserDownload(payload);
        return current;
      }
      if (current.status === 'failed') {
        throw new Error(current.error_message || 'PDF generation failed');
      }
      await sleep(intervalMs, signal);
    }

    throw new Error('PDF generation timed out. Please try again.');
  };
}

/**
 * Hook for managing short URL batches
 */
export default function useShortUrlBatches() {
  const isMounted = useIsMounted();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pdfAbortRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    pdfAbortRef.current = controller;
    return () => controller.abort();
  }, []);

  // Fetch all batches
  const fetchBatches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/short_url_batches`);
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      if (!isMounted.current) return;
      setBatches(data);
      setError(null);
    } catch (err) {
      if (!isMounted.current) return;
      console.error('Error fetching batches:', err);
      setBatches([]);
      setError(err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [isMounted]);

  // Initial fetch
  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  // Generate a new batch. `workflowId` (when provided) pre-assigns the same
  // published User Workflow to every URL in the batch — convenient for
  // dedicated demo / feedback batches.
  const generateBatch = useCallback(async (count, name, workflowId) => {
    const res = await fetchWithAuth(`${API_URL}/v1/admin/short_url_batches/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        count,
        name: name || undefined,
        workflow_id: workflowId || undefined,
      }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || (Array.isArray(data.errors) ? data.errors.join('; ') : null) || res.statusText);
    }
    const result = await res.json();
    await fetchBatches();
    return result;
  }, [fetchBatches]);

  // Export batch as CSV (returns download URL or triggers download)
  const exportBatchCsv = useCallback(async (batchNumber) => {
    const res = await fetchWithAuth(
      `${API_URL}/v1/admin/short_url_batches/${batchNumber}/export.csv`
    );
    if (!res.ok) throw new Error(res.statusText);
    
    // Get the CSV content and trigger download
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `short_urls_batch_${batchNumber}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }, []);

  const startBatchPdfExport = useCallback(async (batchNumber) => {
    const res = await fetchWithAuth(
      `${API_URL}/v1/admin/short_url_batches/${batchNumber}/pdf_exports`,
      { method: 'POST' }
    );
    if (!res.ok) throw new Error(res.statusText);
    return res.json();
  }, []);

  const startSinglePdfExport = useCallback(async (batchNumber, shortUrlId) => {
    const res = await fetchWithAuth(
      `${API_URL}/v1/admin/short_url_batches/${batchNumber}/short_urls/${shortUrlId}/pdf_exports`,
      { method: 'POST' }
    );
    if (!res.ok) throw new Error(res.statusText);
    return res.json();
  }, []);

  const getPdfExport = useCallback(async (exportId) => {
    const res = await fetchWithAuth(`${API_URL}/v1/admin/short_url_pdf_exports/${exportId}`);
    if (!res.ok) throw new Error(res.statusText);
    return res.json();
  }, []);

  const fetchPdfDownload = useCallback(async (exportId) => {
    const res = await fetchWithAuth(`${API_URL}/v1/admin/short_url_pdf_exports/${exportId}/download`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || res.statusText);
    }
    return res.json();
  }, []);

  const triggerBrowserDownload = useCallback(async ({ url, filename }) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to download generated PDF');
    const blob = await res.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'short_urls_export.pdf';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(objectUrl);
    a.remove();
  }, []);

  const waitForPdfExportAndDownload = useMemo(
    () => createWaitForPdfExportAndDownload(getPdfExport, fetchPdfDownload, triggerBrowserDownload),
    [fetchPdfDownload, getPdfExport, triggerBrowserDownload]
  );

  const waitForPdfExportAndDownloadWithAbort = useCallback(
    async (exportId, options = {}) => {
      const signal = options.signal ?? pdfAbortRef.current?.signal;
      return waitForPdfExportAndDownload(exportId, { ...options, signal });
    },
    [waitForPdfExportAndDownload]
  );

  return {
    batches,
    loading,
    error,
    refresh: fetchBatches,
    generateBatch,
    exportBatchCsv,
    startBatchPdfExport,
    startSinglePdfExport,
    getPdfExport,
    fetchPdfDownload,
    waitForPdfExportAndDownload: waitForPdfExportAndDownloadWithAbort,
  };
}

/**
 * Hook for fetching short URLs within a specific batch (lazy loading)
 */
export function useBatchShortUrls(batchNumber) {
  const [shortUrls, setShortUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const fetchShortUrls = useCallback(async () => {
    if (!batchNumber) return;
    
    setLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/short_url_batches/${batchNumber}`
      );
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setShortUrls(data.short_urls || []);
      setLoaded(true);
      setError(null);
    } catch (err) {
      console.error('Error fetching short URLs:', err);
      setShortUrls([]);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [batchNumber]);

  return {
    shortUrls,
    loading,
    loaded,
    error,
    fetchShortUrls,
    refresh: fetchShortUrls,
  };
}

/**
 * Hook for short URL actions (claim, invalidate, set workflow)
 */
export function useShortUrlActions() {
  // Claim a short URL to an asset, optionally also pre-assigning a User Workflow.
  const claimUrl = useCallback(async (shortUrlId, assetId, opts = {}) => {
    const body = { asset_id: assetId };
    if (opts.workflowId) body.workflow_id = opts.workflowId;

    const res = await fetchWithAuth(
      `${API_URL}/v1/admin/short_urls/${shortUrlId}/claim`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    );
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || (Array.isArray(data.errors) ? data.errors.join('; ') : null) || res.statusText);
    }
    return res.json();
  }, []);

  // Set or clear the workflow association on an existing short URL.
  // Pass workflowId = null/undefined to clear.
  const setUrlWorkflow = useCallback(async (shortUrlId, workflowId) => {
    const res = await fetchWithAuth(
      `${API_URL}/v1/admin/short_urls/${shortUrlId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workflow_id: workflowId ?? null }),
      }
    );
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || (Array.isArray(data.errors) ? data.errors.join('; ') : null) || res.statusText);
    }
    return res.json();
  }, []);

  // Invalidate/delete a short URL
  const invalidateUrl = useCallback(async (shortUrlId) => {
    const res = await fetchWithAuth(
      `${API_URL}/v1/admin/short_urls/${shortUrlId}`,
      { method: 'DELETE' }
    );
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || res.statusText);
    }
    return true;
  }, []);

  return { claimUrl, setUrlWorkflow, invalidateUrl };
}

/**
 * Hook for short URL PDF export actions.
 */
export function useShortUrlPdfExports() {
  const pdfAbortRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    pdfAbortRef.current = controller;
    return () => controller.abort();
  }, []);

  const startSinglePdfExport = useCallback(async (batchNumber, shortUrlId) => {
    const res = await fetchWithAuth(
      `${API_URL}/v1/admin/short_url_batches/${batchNumber}/short_urls/${shortUrlId}/pdf_exports`,
      { method: 'POST' }
    );
    if (!res.ok) throw new Error(res.statusText);
    return res.json();
  }, []);

  const getPdfExport = useCallback(async (exportId) => {
    const res = await fetchWithAuth(`${API_URL}/v1/admin/short_url_pdf_exports/${exportId}`);
    if (!res.ok) throw new Error(res.statusText);
    return res.json();
  }, []);

  const fetchPdfDownload = useCallback(async (exportId) => {
    const res = await fetchWithAuth(`${API_URL}/v1/admin/short_url_pdf_exports/${exportId}/download`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || res.statusText);
    }
    return res.json();
  }, []);

  const triggerBrowserDownload = useCallback(async ({ url, filename }) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to download generated PDF');
    const blob = await res.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'short_urls_export.pdf';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(objectUrl);
    a.remove();
  }, []);

  const waitForPdfExportAndDownload = useMemo(
    () => createWaitForPdfExportAndDownload(getPdfExport, fetchPdfDownload, triggerBrowserDownload),
    [fetchPdfDownload, getPdfExport, triggerBrowserDownload]
  );

  const waitForPdfExportAndDownloadWithAbort = useCallback(
    async (exportId, options = {}) => {
      const signal = options.signal ?? pdfAbortRef.current?.signal;
      return waitForPdfExportAndDownload(exportId, { ...options, signal });
    },
    [waitForPdfExportAndDownload]
  );

  return { startSinglePdfExport, waitForPdfExportAndDownload: waitForPdfExportAndDownloadWithAbort };
}
